/* eslint-disable */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../supabase';

export function useClassifyImport() {
  return useMutation({
    mutationFn: async ({ session, rows, uploadedBy }) => {
      const { data, error } = await supabase.rpc('classify_dev_import', {
        p_session: session,
        p_rows: rows,
        p_uploaded_by: uploadedBy || null,
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useStagingRows(session) {
  return useQuery({
    queryKey: ['dev-staging', session],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dev_import_staging')
        .select('*')
        .eq('upload_session', session)
        .order('row_index');
      if (error) throw error;
      return data || [];
    },
    enabled: Boolean(session),
    staleTime: 60_000,
  });
}

export function useUpdateDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stagingId, userAction }) => {
      const { error } = await supabase
        .from('dev_import_staging')
        .update({ user_action: userAction })
        .eq('staging_id', stagingId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dev-staging'] });
    },
  });
}

/**
 * T-1039 — Per-row STAGE override.
 * Read-modify-write: fetch current raw_data, shallow-merge the patch, write back.
 * Mirrors useUpdateDecision pattern; invalidates the same query key.
 *
 * Race-condition note: callers MUST `await` mutateAsync before allowing commit.
 * The DevConflictCard `select` uses `updateMutation.isPending` to lock during write.
 */
export function useUpdateStaging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stagingId, rawDataPatch }) => {
      const { data: current, error: readErr } = await supabase
        .from('dev_import_staging')
        .select('raw_data')
        .eq('staging_id', stagingId)
        .single();
      if (readErr) throw readErr;
      const merged = { ...(current.raw_data || {}), ...rawDataPatch };
      const { error: writeErr } = await supabase
        .from('dev_import_staging')
        .update({ raw_data: merged })
        .eq('staging_id', stagingId);
      if (writeErr) throw writeErr;
      return { stagingId, rawDataPatch };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dev-staging'] });
    },
  });
}

export function useCommitImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ session, decisions, batchId }) => {
      const { data, error } = await supabase.rpc('commit_dev_import', {
        p_session: session,
        p_decisions: decisions,
        p_batch_id: batchId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['developments'] });
      qc.invalidateQueries({ queryKey: ['dev-duplicates'] });
      qc.invalidateQueries({ queryKey: ['devs-paginated'] });
    },
  });
}

// Map UI action labels to the RPC's user_action enum.
export const ACTION_TO_RPC = {
  skip: 'skip',
  import: 'import_anyway',
  replace: 'replace',
  merge: 'merge',
  review_later: 'review',
};
